// Webinar returns for Dashboard → Velocity Metrics: the Webinars page's list-view metrics (Sales Metrics,
// On-Webinar Metrics, Zoom Attendance), with the same values, notes, targets and up/down indicator, for the
// webinars held inside a column's dates. Pure: used by the server (/api/velocity-metrics), the page and its exports.
//
// Which webinars a column holds: every webinar whose date (the Thursday it ran) falls inside the column. A full
// Mon–Sun week holds one webinar, so that column is exactly that webinar's Webinars-page list view (Total view,
// all traffic). A month holds 4–5. A day column holds a webinar only on webinar day.
//
// How several webinars combine (a month column, or the card view over the whole range): counts and dollars add;
// every rate is recomputed from the added counts with the Webinars page's own formula (never averaged).
// Average duration is weighted by attendees; Pitch Start is the average pitch minute; pitch-based rates use only
// webinars with a pitch minute set. Targets come from src/config/webinar-targets.ts, the same table the Webinars
// page uses; "share of opt-ins" targets use the opt-ins of the webinars in the column.
import { WEBINAR_ON_WEBINAR_TARGETS, WEBINAR_SALES_TARGETS, WEBINAR_ZOOM_TARGETS, resolveWebinarTarget, type WebinarAudience, type WebinarTargetIndicator, type WebinarTargetMap } from "../config/webinar-targets";

export type WebinarSalesInput = {
  optins: number; apps: number; fullApps: number; partialApps: number; bookedCalls: number; takenCalls: number;
  noShows: number; cancelledCalls?: number; cancelledBySales: number; cancelledByLead: number; closed: number; deposits: number; cash: number;
  cashFromOptins: number; cashFromApplications: number; cashFromBookedCalls: number; qualifiedApps: number; qualifiedBooked: number;
  closeRate: number; auv: number; cashPerCall: number; appToBookingRate: number; qualifiedToBookingRate: number;
  costPerBooking: number; chargebackRate: number; roas: number;
};
export type WebinarRegTimingInput = { label: string; daysBeforeWebinar: number; registered: number; attended: number; showRate: number };
/** One webinar exactly as the Webinars page loads it (enhanced Total block + funnel + registration timing). */
export type WebinarReturnInput = {
  id: string; name: string; date: string; audience: WebinarAudience;
  sales: WebinarSalesInput;
  totalEocReports: number; totalEocsDue: number; totalEocsTaken: number;
  addToCalendar: number; joinedWhatsApp: number; adSpend: number;
  onWebinar: { registrants: number; cpr: number; attended: number; showRate: number; avgDuration: number; bookingRate: number; pitchMinute: number | null; retentionAtPitch: number; attendeesAtPitch: number };
  dials: { totalDials: number; pickupRate: number };
  funnel: { landingPageVisitors: number; totalOptins: number; optinRate: number; hasZoomData: boolean; totalRegistrants: number; totalAttended: number; showRate: number; avgDuration: number } | null;
  regTiming: WebinarRegTimingInput[];
};
export type WebinarReturns = WebinarReturnInput & { webinars: Array<{ id: string; name: string; date: string }>; pitchWebinars: number; pitchBookedCalls: number; pitchApps: number; pitchFullApps: number; pitchQualifiedApps: number };

const sum = (items: WebinarReturnInput[], pick: (item: WebinarReturnInput) => number) => items.reduce((total, item) => total + (Number(pick(item)) || 0), 0);
const pct = (part: number, whole: number) => (whole > 0 ? (part / whole) * 100 : 0);

/** Combine webinars into one returns object (one webinar comes back unchanged). Null when there is none. */
export function combineWebinarReturns(list: WebinarReturnInput[]): WebinarReturns | null {
  const items = [...new Map(list.map((item) => [item.id, item])).values()].sort((a, b) => a.date.localeCompare(b.date));
  if (!items.length) return null;
  const webinars = items.map(({ id, name, date }) => ({ id, name, date }));
  const pitchItems = items.filter((item) => !!item.onWebinar.pitchMinute);
  const pitchExtras = {
    pitchWebinars: pitchItems.length,
    pitchBookedCalls: sum(pitchItems, (item) => item.sales.bookedCalls), pitchApps: sum(pitchItems, (item) => item.sales.apps),
    pitchFullApps: sum(pitchItems, (item) => item.sales.fullApps), pitchQualifiedApps: sum(pitchItems, (item) => item.sales.qualifiedApps),
  };
  if (items.length === 1) return { ...items[0]!, webinars, ...pitchExtras };

  const s = (key: keyof WebinarSalesInput) => sum(items, (item) => item.sales[key] as number);
  const adSpend = sum(items, (item) => item.adSpend);
  const sales: WebinarSalesInput = {
    optins: s("optins"), apps: s("apps"), fullApps: s("fullApps"), partialApps: s("partialApps"), bookedCalls: s("bookedCalls"), takenCalls: s("takenCalls"),
    noShows: s("noShows"), cancelledCalls: s("cancelledCalls"), cancelledBySales: s("cancelledBySales"), cancelledByLead: s("cancelledByLead"), closed: s("closed"), deposits: s("deposits"), cash: s("cash"),
    cashFromOptins: s("cashFromOptins"), cashFromApplications: s("cashFromApplications"), cashFromBookedCalls: s("cashFromBookedCalls"),
    qualifiedApps: s("qualifiedApps"), qualifiedBooked: s("qualifiedBooked"),
    closeRate: 0, auv: 0, cashPerCall: 0, appToBookingRate: 0, qualifiedToBookingRate: 0, costPerBooking: 0, chargebackRate: 0, roas: 0,
  };
  // Same formulas as the webinar service's calcRates (wins = closes + deposits).
  const wins = sales.closed + sales.deposits;
  sales.closeRate = pct(wins, sales.takenCalls);
  sales.auv = wins > 0 ? sales.cash / wins : 0;
  sales.cashPerCall = sales.takenCalls > 0 ? sales.cash / sales.takenCalls : 0;
  sales.appToBookingRate = pct(sales.bookedCalls, sales.fullApps);
  sales.qualifiedToBookingRate = pct(sales.qualifiedBooked, sales.qualifiedApps);
  sales.chargebackRate = pct(sum(items, (item) => item.sales.chargebackRate * item.sales.closed / 100), sales.closed);
  const paidCash = sum(items, (item) => item.sales.roas * item.adSpend);
  sales.roas = adSpend > 0 ? paidCash / adSpend : 0;
  const paidBooked = sum(items, (item) => (item.sales.costPerBooking > 0 ? item.adSpend / item.sales.costPerBooking : 0));
  sales.costPerBooking = paidBooked > 0 ? adSpend / paidBooked : 0;

  const registrants = sum(items, (item) => item.onWebinar.registrants);
  const attended = sum(items, (item) => item.onWebinar.attended);
  const attendeesAtPitch = sum(pitchItems, (item) => item.onWebinar.attendeesAtPitch);
  const pitchAttended = sum(pitchItems, (item) => item.onWebinar.attended);
  const fallbackDenominator = sum(items, (item) => item.sales.apps);
  const onWebinar = {
    registrants, attended,
    cpr: registrants > 0 && adSpend > 0 ? adSpend / registrants : 0,
    showRate: pct(attended, registrants),
    avgDuration: attended > 0 ? sum(items, (item) => item.onWebinar.avgDuration * item.onWebinar.attended) / attended : 0,
    pitchMinute: pitchItems.length ? Math.round(sum(pitchItems, (item) => item.onWebinar.pitchMinute ?? 0) / pitchItems.length) : null,
    attendeesAtPitch,
    retentionAtPitch: pct(attendeesAtPitch, pitchAttended),
    bookingRate: pct(sales.bookedCalls, attendeesAtPitch > 0 ? attendeesAtPitch : (fallbackDenominator || 1)),
  };
  const totalDials = sum(items, (item) => item.dials.totalDials);
  const dials = { totalDials, pickupRate: pct(sum(items, (item) => item.dials.pickupRate * item.dials.totalDials / 100), totalDials) };

  const funnels = items.map((item) => item.funnel).filter((funnel): funnel is NonNullable<WebinarReturnInput["funnel"]> => !!funnel);
  const withVisitors = funnels.filter((funnel) => funnel.landingPageVisitors > 0);
  const zoom = funnels.filter((funnel) => funnel.hasZoomData);
  const zoomAttended = zoom.reduce((total, funnel) => total + funnel.totalAttended, 0);
  const zoomRegistrants = zoom.reduce((total, funnel) => total + funnel.totalRegistrants, 0);
  const landingPageVisitors = withVisitors.reduce((total, funnel) => total + funnel.landingPageVisitors, 0);
  const funnel = funnels.length ? {
    landingPageVisitors,
    totalOptins: funnels.reduce((total, f) => total + f.totalOptins, 0),
    optinRate: pct(withVisitors.reduce((total, f) => total + f.totalOptins, 0), landingPageVisitors),
    hasZoomData: zoom.length > 0,
    totalRegistrants: zoomRegistrants,
    totalAttended: zoomAttended,
    showRate: pct(zoomAttended, zoomRegistrants),
    avgDuration: zoomAttended > 0 ? zoom.reduce((total, f) => total + f.avgDuration * f.totalAttended, 0) / zoomAttended : 0,
  } : null;

  const buckets = new Map<string, WebinarRegTimingInput>();
  for (const point of items.flatMap((item) => (item.funnel?.hasZoomData ? item.regTiming : []))) {
    const bucket = buckets.get(point.label) ?? { label: point.label, daysBeforeWebinar: point.daysBeforeWebinar, registered: 0, attended: 0, showRate: 0 };
    bucket.registered += point.registered; bucket.attended += point.attended; bucket.daysBeforeWebinar = Math.max(bucket.daysBeforeWebinar, point.daysBeforeWebinar);
    buckets.set(point.label, bucket);
  }
  const regTiming = [...buckets.values()].map((bucket) => ({ ...bucket, showRate: pct(bucket.attended, bucket.registered) })).sort((a, b) => b.daysBeforeWebinar - a.daysBeforeWebinar);

  return {
    id: webinars.map((w) => w.id).join("+"), name: `${items.length} webinars`, date: items[0]!.date,
    // Every webinar is cold traffic unless all of them are warm.
    audience: items.every((item) => item.audience === "warm") ? "warm" : "cold",
    sales, totalEocReports: sum(items, (item) => item.totalEocReports), totalEocsDue: sum(items, (item) => item.totalEocsDue), totalEocsTaken: sum(items, (item) => item.totalEocsTaken),
    addToCalendar: sum(items, (item) => item.addToCalendar), joinedWhatsApp: sum(items, (item) => item.joinedWhatsApp), adSpend,
    onWebinar, dials, funnel, regTiming, webinars, ...pitchExtras,
  };
}

export type WebinarReturnRow = { section: WebinarReturnSection; metric: string; value: string; notes: string; rawValue?: number; target?: string; indicator?: WebinarTargetIndicator; lo?: number; hi?: number };
export type WebinarReturnSection = "Sales Metrics" | "On-Webinar Metrics" | "Zoom Attendance";
export const WEBINAR_RETURN_SECTIONS: WebinarReturnSection[] = ["Sales Metrics", "On-Webinar Metrics", "Zoom Attendance"];

// The Webinars page's own formatters.
export const fmtWebinarMoney = (n: number) => {
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `$${(n / 1_000).toFixed(1)}K`;
  return `$${Math.round(n).toLocaleString("en-US")}`;
};
const fmt$ = fmtWebinarMoney;
const fmtPct = (n: number) => `${n.toFixed(1)}%`;
const fmtN = (n: number) => Math.round(n).toLocaleString("en-US");

/**
 * The Webinars page list-view rows (Total view, all traffic) for one returns object, in the page's order, with the
 * page's labels, values, notes and targets. Rows the page hides when empty (Landing Page Visitors, No Shows,
 * Deposits, Ad Spend, …) are hidden by the same rule.
 */
export function buildWebinarReturnRows(ret: WebinarReturns): WebinarReturnRow[] {
  const rows: WebinarReturnRow[] = [];
  const t = ret.sales; const f = ret.funnel; const ow = ret.onWebinar;
  const optins = t.optins;
  const push = (section: WebinarReturnSection, metric: string, value: string, notes = "", rawValue?: number, targets?: WebinarTargetMap) => {
    const target = targets ? resolveWebinarTarget(metric, rawValue, targets, ret.audience, optins) : null;
    rows.push({ section, metric, value, notes, rawValue, ...(target ? { target: target.rangeStr, indicator: target.indicator, lo: target.lo, hi: target.hi } : {}) });
  };
  const S = (metric: string, value: string, notes: string, rawValue?: number) => push("Sales Metrics", metric, value, notes, rawValue, WEBINAR_SALES_TARGETS);

  // Sales Metrics (the page's salesKpis, Total view)
  const showRate = t && ret.totalEocsDue > 0 ? (ret.totalEocsTaken / ret.totalEocsDue) * 100 : 0;
  // Booking status (Joey, Oct 19 2026): appointments whose latest Call Bookings status is cancelled.
  const cancelled = t.cancelledCalls ?? (t.cancelledBySales + t.cancelledByLead);
  S("Opt-ins", fmtN(t.optins), "", t.optins);
  const visitors = f?.landingPageVisitors ?? 0; const optinRate = f?.optinRate ?? 0;
  if (visitors > 0) S("Landing Page Visitors", fmtN(visitors), "", visitors);
  if (optinRate > 0) S("Opt-in Rate", fmtPct(optinRate), visitors > 0 ? `${fmtN(t.optins)} / ${fmtN(visitors)}` : "", optinRate);
  S("Total Apps", fmtN(t.apps), "", t.apps);
  S("Full Apps", fmtN(t.fullApps), "", t.fullApps);
  S("Qualified Apps", fmtN(t.qualifiedApps), "", t.qualifiedApps);
  S("Partial Apps", fmtN(t.partialApps), "", t.partialApps);
  S("Webinar Bookings", fmtN(t.bookedCalls), "", t.bookedCalls);
  S("End of Call Reports Filled", fmtN(ret.totalEocReports), "", ret.totalEocReports);
  S("Calls Taken", fmtN(ret.totalEocsTaken), "", ret.totalEocsTaken);
  S("Show Rate", fmtPct(showRate), `${fmtN(ret.totalEocsTaken)} / ${fmtN(ret.totalEocsDue)}`, showRate);
  if (t.noShows > 0) S("No Shows", fmtN(t.noShows), "", t.noShows);
  if (cancelled > 0) S("Cancelled Calls", `${fmtN(cancelled)} (${t.bookedCalls > 0 ? `${Math.round(cancelled / t.bookedCalls * 100)}%` : "—"})`, `of ${fmtN(t.bookedCalls)} webinar bookings`, cancelled);
  S("Closes", fmtN(t.closed), "", t.closed);
  if (t.deposits > 0) S("Deposits", fmtN(t.deposits), "", t.deposits);
  S("Close Rate", fmtPct(t.closeRate), "", t.closeRate);
  S("Cash", fmt$(t.cash), "", t.cash);
  S("Cash from Opt-ins", fmt$(t.cashFromOptins), "", t.cashFromOptins);
  S("Cash from Applications", fmt$(t.cashFromApplications), "", t.cashFromApplications);
  S("Cash from Booked Calls", fmt$(t.cashFromBookedCalls), "", t.cashFromBookedCalls);
  S("AOV", t.auv > 0 ? fmt$(t.auv) : "—", "", t.auv);
  S("Cash/Call", t.cashPerCall > 0 ? fmt$(t.cashPerCall) : "—", "", t.cashPerCall);
  if (t.costPerBooking > 0) S("Cost/Booking", fmt$(t.costPerBooking), "", t.costPerBooking);
  S("Full App→Booking", fmtPct(t.appToBookingRate), `${fmtN(t.fullApps)} / ${fmtN(t.bookedCalls)}`, t.appToBookingRate);
  const ob = pct(t.bookedCalls, t.optins); S("Optin→Booking", fmtPct(ob), `${fmtN(t.optins)} / ${fmtN(t.bookedCalls)}`, ob);
  const ofa = pct(t.fullApps, t.optins); S("Opt-in→Full App", fmtPct(ofa), `${fmtN(t.optins)} / ${fmtN(t.fullApps)}`, ofa);
  const oqa = pct(t.qualifiedApps, t.optins); S("Optin→Qual App", fmtPct(oqa), `${fmtN(t.optins)} / ${fmtN(t.qualifiedApps)}`, oqa);
  S("Qual→Booking", fmtPct(t.qualifiedToBookingRate), `${fmtN(t.qualifiedBooked)} / ${fmtN(t.qualifiedApps)} qual`, t.qualifiedToBookingRate);
  const cal = (ret.addToCalendar / Math.max(t.optins, 1)) * 100;
  S("Add to Calendar", ret.addToCalendar > 0 ? fmtPct(cal) : "—", `${fmtN(ret.addToCalendar)} / ${fmtN(t.optins)}`, cal);
  const wa = (ret.joinedWhatsApp / Math.max(t.optins, 1)) * 100;
  S("Joined WhatsApp", ret.joinedWhatsApp > 0 ? fmtPct(wa) : "—", `${fmtN(ret.joinedWhatsApp)} / ${fmtN(t.optins)}`, wa);
  if (ret.adSpend > 0) { S("Ad Spend", fmt$(ret.adSpend), "", ret.adSpend); S("ROAS", t.roas > 0 ? `${t.roas.toFixed(1)}x` : "—", "", t.roas); }
  if (t.chargebackRate > 0) S("Chargeback %", fmtPct(t.chargebackRate), "", t.chargebackRate);

  // On-Webinar Metrics
  const O = (metric: string, value: string, notes: string, rawValue?: number) => push("On-Webinar Metrics", metric, value, notes, rawValue, rawValue === undefined ? undefined : WEBINAR_ON_WEBINAR_TARGETS);
  const pitchNote = ret.webinars.length > 1 && ret.pitchWebinars < ret.webinars.length ? ` (${ret.pitchWebinars} of ${ret.webinars.length} webinars have a pitch minute)` : "";
  O("Opt-ins", fmtN(ow.registrants), "");
  O("CPR", ow.cpr > 0 ? fmt$(ow.cpr) : "—", "");
  O("Attended", fmtN(ow.attended), "", ow.attended);
  O("Show Rate", fmtPct(ow.showRate), `${fmtN(ow.attended)} / ${fmtN(ow.registrants)}`, ow.showRate);
  O("Avg Duration", `${Math.round(ow.avgDuration)}m`, "", ow.avgDuration);
  if (ow.pitchMinute) {
    O("Pitch Start", `${ow.pitchMinute}m`, ret.webinars.length > 1 ? `average of ${ret.pitchWebinars} webinar${ret.pitchWebinars === 1 ? "" : "s"}` : "", ow.pitchMinute);
    O("@ Pitch", fmtPct(ow.retentionAtPitch), fmtN(ow.attendeesAtPitch) + pitchNote, ow.retentionAtPitch);
    const booked = ret.pitchBookedCalls;
    const br = pct(booked, ow.attendeesAtPitch);
    O("Booking Rate", fmtPct(br), `${fmtN(ow.attendeesAtPitch)} / ${fmtN(booked)}`, br);
  } else {
    O("Booking Rate", fmtPct(ow.bookingRate), "", ow.bookingRate);
  }
  if (ow.attendeesAtPitch > 0) {
    const ta = pct(ret.pitchApps, ow.attendeesAtPitch); O("Total App Rate", fmtPct(ta), `${fmtN(ret.pitchApps)} / ${fmtN(ow.attendeesAtPitch)}`, ta);
    const fa = pct(ret.pitchFullApps, ow.attendeesAtPitch); O("Full App Rate", fmtPct(fa), `${fmtN(ret.pitchFullApps)} / ${fmtN(ow.attendeesAtPitch)}`, fa);
    const qa = pct(ret.pitchQualifiedApps, ow.attendeesAtPitch); O("Qual App Rate", fmtPct(qa), `${fmtN(ow.attendeesAtPitch)} / ${fmtN(ret.pitchQualifiedApps)}`, qa);
  }
  if (ret.dials.totalDials > 0) { O("Total Dials", fmtN(ret.dials.totalDials), ""); O("Pickup Rate", fmtPct(ret.dials.pickupRate), ""); }

  // Zoom Attendance
  if (f?.hasZoomData) {
    const Z = (metric: string, value: string, notes: string, rawValue?: number) => push("Zoom Attendance", metric, value, notes, rawValue, rawValue === undefined ? undefined : WEBINAR_ZOOM_TARGETS);
    Z("Registrants", fmtN(f.totalRegistrants), "");
    Z("Attended", fmtN(f.totalAttended), "", f.totalAttended);
    Z("Show Rate", fmtPct(f.showRate), "", f.showRate);
    Z("Avg Duration", `${Math.round(f.avgDuration)}m`, "", f.avgDuration);
    for (const point of ret.regTiming) Z(point.label, `${point.showRate.toFixed(1)}%`, `${point.attended} showed / ${point.registered} registered`, point.showRate);
  }
  return rows;
}
